import { makeStyles } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import NestedMenuItem from "components/nested-menu-item";
import { DATATEST } from "constants/integration";
import { View } from "constants/product-collection";
import { merge } from "glamor";
import Divider from "material-ui/Divider";
import ExpandMoreIcon from "material-ui/svg-icons/navigation/expand-more";
import * as React from "react";
import theme from "styles/theme";
import Button from "./button";

export enum MenuItemType {
    Divider = "divider",
}

/**
 * Used to style nested menu Item
 */
const useStyles = makeStyles({
    root: {
        fontSize: 16,
        fontFamily: "'Lato', sans-serif",
        fontWeight: 400,
        paddingTop: 0,
        letterSpacing: 0.2,
        paddingBottom: 0,
        "& svg": {
            marginLeft: "auto",
        },
        "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: theme.palette.alternateTextColor,
        },
        "&.MuiMenuItem-root": {
            minHeight: "48px",
        },
    },
});

/**
 * Level 2 nodes
 */
export interface IViewSubItem {
    name: string;
    /**
     * Flag used to disable individual menu options
     * @default false
     */
    disabled?: boolean;
    selected?: boolean;
    rightIcon?: React.ReactNode;
    leftIcon?: React.ReactNode;
    dataTest?: string;
    onClick?: (event?: React.MouseEvent, params?: any[]) => void;
    style?: React.CSSProperties;
    keepOpenMenuOnSelect?: boolean;
}

/**
 * Level 1 nodes
 */
export interface INestedViewSubItem {
    name: string;
    onClick?: (event?: React.MouseEvent, params?: any[]) => void;
    /**
     * Flag used to disable individual menu options
     * @default false
     */
    disabled?: boolean;
    selected?: boolean;
    nestedItem?: IViewSubItem[];
    dataTest?: string;
    rightIcon?: React.ReactNode;
}

/**
 * NestedViewButton property definition
 */
interface IProps {
    buttonActive?: boolean;
    dataTest?: string;
    dataTestMoreButton?: string;
    dropdownActive?: boolean;
    icon: React.ReactNode;
    name: string;
    onClick?: (event?: React.MouseEvent, params?: any[]) => void;
    subItems: INestedViewSubItem[];
    view: View;
    style?: React.CSSProperties;
}

/**
 * Clone of ViewButton that handles nested menus. This was created separately since
 * we need to use the new @material-ui/core components to render the nested menus properly.
 * This can break compatibility hence why this file has been created.
 *
 * @param buttonActive - Enables/disables button
 * @param dataTest - Name of button used for testing
 * @param dropdownActive - Enable/disable drop-down
 * @param icon - Button icon
 * @param name - Button name
 * @param onClick - Action to perform after click
 * @param subItems - Arrays of arrays to represent nested menus
 * @param view - Name of view
 *
 * @returns Material-UI Button and chevron with nested menus
 *
 * @todo Update to handle multiple nesting levels since now it can only handle 2 levels of nesting
 */
const NestedViewButton: React.FunctionComponent<IProps> = React.memo(function NestedViewButton(props: IProps) {
    const classes = useStyles(props);
    const {
        buttonActive,
        onClick: buttonOnClick,
        dropdownActive,
        name,
        icon,
        dataTest,
        dataTestMoreButton,
        subItems,
        view,
        style = {},
    } = props;
    const styles = React.useMemo(
        () =>
            merge({
                display: "flex",
                alignItems: "center",
                ...style,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleOpen = (event) => {
        if (dropdownActive) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose =
        (onMenuClick?: (passThruEvent: React.MouseEvent, passThruParams: any[]) => void, ...params: any[]) =>
        (event: React.MouseEvent) => {
            // close menu
            setAnchorEl(null);

            // call onMenuClick if available
            if (onMenuClick) {
                onMenuClick(event, params);
            }
        };

    const anchorRef = React.useRef();

    return (
        <div className="viewActionWrapper" {...styles}>
            <Button
                active={buttonActive}
                dataView={view as unknown as string}
                onClick={buttonOnClick}
                label={name}
                dataTest={dataTest}
                icon={icon}
            />
            {subItems.length > 0 && (
                <>
                    <IconButton
                        data-test={dataTestMoreButton}
                        style={{ width: "20px", height: "20px", padding: 0 }}
                        disabled={!dropdownActive}
                        onClick={handleOpen}
                        ref={anchorRef}
                    >
                        <ExpandMoreIcon />
                    </IconButton>
                    <Menu
                        anchorEl={anchorRef.current}
                        keepMounted={true}
                        open={Boolean(anchorEl)}
                        onClose={handleClose()}
                        getContentAnchorEl={null}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                        }}
                        data-test={`${DATATEST.ASSORTMENTS.NESTED_VIEW_BTN}--${view}`}
                    >
                        {subItems.map((subItem, idx) =>
                            subItem.name === MenuItemType.Divider ? (
                                <Divider key={idx} />
                            ) : subItem.nestedItem ? (
                                <NestedMenuItem
                                    key={idx}
                                    className={classes.root}
                                    label={subItem.name}
                                    parentMenuOpen={!!anchorEl}
                                    disabled={subItem.disabled}
                                    dataTest={subItem.dataTest}
                                >
                                    {subItem.nestedItem.map((subNestedItem, subNestedIdx) =>
                                        subNestedItem.name === MenuItemType.Divider ? (
                                            <Divider key={subNestedIdx} />
                                        ) : (
                                            <MenuItem
                                                key={subNestedIdx}
                                                className={classes.root}
                                                selected={!subNestedItem.disabled && subNestedItem.selected}
                                                disabled={subNestedItem.disabled}
                                                data-test={subNestedItem.dataTest}
                                                onClick={
                                                    subNestedItem.keepOpenMenuOnSelect
                                                        ? subNestedItem.onClick
                                                        : handleClose(subNestedItem.onClick)
                                                }
                                                style={subNestedItem.style}
                                            >
                                                {subNestedItem?.leftIcon && (
                                                    <div style={{ float: "left" }}>{subNestedItem.leftIcon}</div>
                                                )}
                                                <div style={{ marginLeft: subNestedItem?.leftIcon ? "20px" : "0" }}>
                                                    {subNestedItem.name}
                                                </div>
                                                {subNestedItem.rightIcon}
                                            </MenuItem>
                                        )
                                    )}
                                </NestedMenuItem>
                            ) : (
                                <MenuItem
                                    key={idx}
                                    className={classes.root}
                                    selected={!subItem.disabled && subItem.selected}
                                    disabled={subItem.disabled}
                                    onClick={handleClose(subItem.onClick)}
                                    data-test={subItem.dataTest}
                                >
                                    {subItem.name}
                                    {subItem.rightIcon}
                                </MenuItem>
                            )
                        )}
                    </Menu>
                </>
            )}
        </div>
    );
});

export default NestedViewButton;
